import type { RefObject } from "react";

import { translate } from "app/filters/i18nFilter";
import { AIAgentPromptGroupContainer } from "app/pages/sales-intelligence/modules/ai-agent/containers/AIAgentPromptGroup";
import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";

import { AIAgentPromptCard } from "../../../../components/AIAgentPromptCard";
import { ContextualSuggestionsContainer } from "../../containers/ContextualSuggestionsStrategy";
import type { MdComponentProps } from "../../types/mdComponentProps";
import type { ChatThreadModel } from "../../types/models";

import { ChatMessages } from "./components/ChatMessages";
import { StyledFiltersChat } from "./styles";

type Props<T extends IPlainObject = IPlainObject> = Pick<ChatThreadModel, "messages" | "isLoading" | "isPending"> & {
    withInitialInputPlaceholder: boolean;
    mdComponentProps: MdComponentProps<T>;
    onMessageSubmit: (text: string) => void;

    containerRef: RefObject<HTMLDivElement | null>;
    bottomRef: RefObject<HTMLDivElement | null>;
    lastUserMessageRef: RefObject<HTMLDivElement | null>;
    lastAiMessageRef: RefObject<HTMLDivElement | null>;
};

export const AIAgentChat = <T extends IPlainObject = IPlainObject>({
    isLoading,
    isPending,
    messages,
    withInitialInputPlaceholder,
    containerRef,
    bottomRef,
    lastUserMessageRef,
    lastAiMessageRef,
    mdComponentProps,
    onMessageSubmit,
}: Props<T>) => {
    const isChatFetching = isPending || isLoading;

    const inputPlaceholderKey = withInitialInputPlaceholder
        ? "si.agent.leads.input.placeholder.text.initial"
        : "si.agent.leads.input.placeholder.text";

    return (
        <>
            <StyledFiltersChat ref={containerRef}>
                <ChatMessages
                    isLoading={isLoading || isPending}
                    messages={messages}
                    mdComponentProps={mdComponentProps}
                    lastUserMessageRef={lastUserMessageRef}
                    lastAiMessageRef={lastAiMessageRef}
                />
            </StyledFiltersChat>

            <div ref={bottomRef} />

            <AIAgentPromptCard>
                <ContextualSuggestionsContainer onSuggestionClick={onMessageSubmit} />

                <AIAgentPromptGroupContainer
                    isFocusTriggered={!isLoading}
                    isInputLocked={isChatFetching}
                    placeholder={translate(inputPlaceholderKey)}
                    onSubmit={onMessageSubmit}
                />
            </AIAgentPromptCard>
        </>
    );
};
